import '@fontsource-variable/dm-sans';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { initApi } from './api.ts';
import { currentToken } from './auth.ts';
import { loadConfig } from './config.ts';
import { USER_FILTERS, type UserFilter } from './format.ts';
import { AuditPage } from './pages/audit.tsx';
import { BookingsPage } from './pages/bookings.tsx';
import { BuildingPage } from './pages/building.tsx';
import { BuildingsPage } from './pages/buildings.tsx';
import { ContractsPage } from './pages/contracts.tsx';
import { DepartmentsPage } from './pages/departments.tsx';
import { DevicesPage } from './pages/devices.tsx';
import { HomePage } from './pages/home.tsx';
import { IntegrationsPage } from './pages/integrations.tsx';
import { OrganizationsPage } from './pages/organizations.tsx';
import { SignInPage } from './pages/sign-in.tsx';
import { UsersPage } from './pages/users.tsx';
import { Shell } from './shell.tsx';
import { theme } from './theme.ts';

const root = createRootRoute({ component: Shell });

const text = (value: unknown): string | undefined => (typeof value === 'string' && value ? value : undefined);

const buildingRoute = createRoute({
  getParentRoute: () => root,
  path: '/buildings/$buildingId',
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({ tab: text(search.tab) }),
  component: function Building() {
    const { buildingId } = buildingRoute.useParams();
    return <BuildingPage buildingId={buildingId} />;
  },
});

const usersRoute = createRoute({
  getParentRoute: () => root,
  path: '/users',
  validateSearch: (search: Record<string, unknown>): { filter?: UserFilter; q?: string } => ({
    filter: USER_FILTERS.find((filter) => filter === search.filter),
    q: text(search.q),
  }),
  component: UsersPage,
});

const bookingsRoute = createRoute({
  getParentRoute: () => root,
  path: '/bookings',
  validateSearch: (
    search: Record<string, unknown>,
  ): { buildingId?: string; day?: string; status?: string } => ({
    buildingId: text(search.buildingId),
    day: text(search.day),
    status: text(search.status),
  }),
  component: BookingsPage,
});

const devicesRoute = createRoute({
  getParentRoute: () => root,
  path: '/devices',
  validateSearch: (search: Record<string, unknown>): { buildingId?: string } => ({
    buildingId: text(search.buildingId),
  }),
  component: DevicesPage,
});

const auditRoute = createRoute({
  getParentRoute: () => root,
  path: '/audit',
  validateSearch: (search: Record<string, unknown>): { entity?: string; q?: string } => ({
    entity: text(search.entity),
    q: text(search.q),
  }),
  component: AuditPage,
});

const routes = [
  createRoute({ getParentRoute: () => root, path: '/', component: HomePage }),
  createRoute({ getParentRoute: () => root, path: '/organizations', component: OrganizationsPage }),
  createRoute({ getParentRoute: () => root, path: '/buildings', component: BuildingsPage }),
  buildingRoute,
  bookingsRoute,
  usersRoute,
  createRoute({ getParentRoute: () => root, path: '/departments', component: DepartmentsPage }),
  devicesRoute,
  createRoute({ getParentRoute: () => root, path: '/contracts', component: ContractsPage }),
  createRoute({ getParentRoute: () => root, path: '/integrations', component: IntegrationsPage }),
  auditRoute,
];

const router = createRouter({ routeTree: root.addChildren(routes) });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const config = await loadConfig();
const signedIn = currentToken() !== null;
if (signedIn) initApi(config.apiUrl);
createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light">
      {signedIn ? (
        <>
          <Notifications position="top-right" />
          <QueryClientProvider client={new QueryClient({ defaultOptions: { queries: { retry: false } } })}>
            <ModalsProvider>
              <RouterProvider router={router} />
            </ModalsProvider>
          </QueryClientProvider>
        </>
      ) : (
        <SignInPage apiUrl={config.apiUrl} />
      )}
    </MantineProvider>
  </StrictMode>,
);
